/**
 * Curves the way an illustrator draws them: a closed path of a few anchor
 * points joined by cubic Béziers. `fitPath` turns a dense border polygon (rail
 * slices, raster contours) into the fewest anchors that still follow it within
 * a tolerance — corners stay sharp, everything else is one smooth curve —
 * using Schneider's least-squares fitting (Graphics Gems, 1990).
 *
 * Storage: `pts` are the anchors (flat x,y), `h` the handles, four numbers per
 * anchor: the control point arriving at it (in) and the one leaving it (out).
 * Segment i runs from anchor i via out_i and in_{i+1} to anchor i+1.
 */
import type { Vec } from '../../shared/doc.ts';

export interface CurvePath {
  pts: number[];
  h: number[];
}

interface Cubic {
  p0: Vec;
  c1: Vec;
  c2: Vec;
  p3: Vec;
}

const v = (x: number, y: number): Vec => ({ x, y });
const add = (a: Vec, b: Vec): Vec => v(a.x + b.x, a.y + b.y);
const sub = (a: Vec, b: Vec): Vec => v(a.x - b.x, a.y - b.y);
const mul = (a: Vec, k: number): Vec => v(a.x * k, a.y * k);
const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
const len = (a: Vec): number => Math.hypot(a.x, a.y);
const unit = (a: Vec): Vec => {
  const l = len(a) || 1;
  return v(a.x / l, a.y / l);
};

/**
 * Indices of the polygon's corners: vertices where the border's direction over
 * the `reach` px before them differs from its direction over the `reach` px
 * after them by at least `minTurnDeg`. A jog or wiggle a few px long has the
 * same direction on both sides and is not a corner; a band's square end is.
 */
export function cornersOf(pts: number[], reach: number, minTurnDeg = 35): number[] {
  const n = pts.length / 2;
  if (n < 3) return [];
  const P = (i: number): Vec => v(pts[2 * (((i % n) + n) % n)]!, pts[2 * (((i % n) + n) % n) + 1]!);
  const near = Math.max(1.5, reach * 0.2);
  // the border between `near` and `reach` px away from vertex i, on one side: its direction and start
  const arm = (i: number, dir: 1 | -1): { d: Vec; from: Vec } | null => {
    let acc = 0,
      k = i,
      from: Vec | null = null,
      to: Vec | null = null;
    for (let step = 0; step < n - 1; step++) {
      const j = k + dir;
      const seg = len(sub(P(j), P(k)));
      if (from === null && acc + seg >= near) {
        const t = seg ? (near - acc) / seg : 0;
        from = add(P(k), mul(sub(P(j), P(k)), Math.max(0, Math.min(1, t))));
      }
      if (acc + seg >= reach) {
        const t = seg ? (reach - acc) / seg : 0;
        to = add(P(k), mul(sub(P(j), P(k)), Math.max(0, Math.min(1, t))));
        break;
      }
      acc += seg;
      k = j;
    }
    if (!from || !to) return null;
    const d = sub(to, from);
    return len(d) < 1e-6 ? null : { d: unit(mul(d, dir)), from };
  };
  const armDir = (i: number, dir: 1 | -1): Vec | null => arm(i, dir)?.d ?? null;
  const minCos = Math.cos((minTurnDeg * Math.PI) / 180);
  const turnAt = (i: number): number => {
    const a = armDir(i, -1),
      b = armDir(i, 1);
    return a && b ? dot(a, b) : 1;
  };
  // the corner proper is where the two arms' lines meet: the vertex nearest that point
  const refine = (i: number): number => {
    const a = arm(i, -1),
      b = arm(i, 1);
    if (!a || !b) return i;
    const den = a.d.x * b.d.y - a.d.y * b.d.x;
    if (Math.abs(den) < 1e-6) return i;
    const t = ((b.from.x - a.from.x) * b.d.y - (b.from.y - a.from.y) * b.d.x) / den;
    const x = add(a.from, mul(a.d, t));
    let best = i,
      bd = len(sub(P(i), x));
    for (let k = -3; k <= 3; k++) {
      const dd = len(sub(P(i + k), x));
      if (dd < bd) {
        bd = dd;
        best = ((i + k) % n + n) % n;
      }
    }
    return best;
  };
  const out: number[] = [];
  for (let i = 0; i < n; i++) if (turnAt(i) <= minCos) out.push(i);
  // one bend sampled by several vertices is one corner: keep the sharpest of each cluster
  const along = (a: number, b: number) => {
    let acc = 0;
    for (let k = a; k < b; k++) acc += len(sub(P(k + 1), P(k)));
    return acc;
  };
  const cluster = 0.6 * reach;
  const merged: number[] = [];
  for (const i of out) {
    const prev = merged[merged.length - 1];
    if (prev !== undefined && along(prev, i) < cluster) {
      if (turnAt(i) < turnAt(prev)) merged[merged.length - 1] = i;
      continue;
    }
    merged.push(i);
  }
  if (merged.length >= 2 && along(merged[merged.length - 1]!, n) + along(0, merged[0]!) < cluster) {
    // the cluster wraps around the start
    if (turnAt(merged[0]!) < turnAt(merged[merged.length - 1]!)) merged.pop();
    else merged.shift();
  }
  const refined = merged.map(refine).sort((p, q) => p - q);
  return refined.filter((v, k) => k === 0 || v !== refined[k - 1]);
}

/** Fit a closed border polygon with the fewest cubic segments that stay within `tol` px of it. */
export function fitPath(poly: number[], tol = 1.2, cornerReach = 12): CurvePath {
  const pts = dedupe(poly, 0.3);
  const n = pts.length / 2;
  if (n < 3) return straight(pts);
  const P = (i: number): Vec => v(pts[2 * (((i % n) + n) % n)]!, pts[2 * (((i % n) + n) % n) + 1]!);
  let starts = cornersOf(pts, cornerReach);
  const smoothLoop = starts.length === 0;
  if (smoothLoop) starts = [0];
  const segs: Cubic[] = [];
  for (let k = 0; k < starts.length; k++) {
    const a = starts[k]!,
      b = starts[(k + 1) % starts.length]!;
    const run: Vec[] = [P(a)];
    for (let i = a + 1; ; i++) {
      run.push(P(i));
      if (((i % n) + n) % n === b) break;
      if (run.length > n + 1) break;
    }
    // end tangents over an arm of the run, never from a single (possibly jogged) border segment
    const runLen = polyLen(run);
    const arm = Math.max(2, Math.min(0.5 * cornerReach, 0.25 * runLen));
    let tan1: Vec, tan2: Vec;
    if (smoothLoop) {
      const t = unit(sub(pointAlong(run, arm), pointAlong(run, runLen - arm)));
      tan1 = t;
      tan2 = mul(t, -1);
    } else {
      tan1 = unit(sub(pointAlong(run, arm), run[0]!));
      tan2 = unit(sub(pointAlong(run, runLen - arm), run[run.length - 1]!));
    }
    fitCubic(run, 0, run.length - 1, tan1, tan2, tol, segs);
  }
  const out: number[] = [],
    h: number[] = [];
  const m = segs.length;
  for (let i = 0; i < m; i++) {
    const s = segs[i]!,
      prev = segs[(i - 1 + m) % m]!;
    out.push(s.p0.x, s.p0.y);
    h.push(prev.c2.x, prev.c2.y, s.c1.x, s.c1.y);
  }
  return { pts: out, h };
}

function polyLen(run: Vec[]): number {
  let l = 0;
  for (let i = 1; i < run.length; i++) l += len(sub(run[i]!, run[i - 1]!));
  return l;
}
function pointAlong(run: Vec[], s: number): Vec {
  let acc = 0;
  for (let i = 1; i < run.length; i++) {
    const l = len(sub(run[i]!, run[i - 1]!));
    if (acc + l >= s) return add(run[i - 1]!, mul(sub(run[i]!, run[i - 1]!), l ? (s - acc) / l : 0));
    acc += l;
  }
  return run[run.length - 1]!;
}

function straight(pts: number[]): CurvePath {
  const h: number[] = [];
  for (let i = 0; i < pts.length; i += 2) h.push(pts[i]!, pts[i + 1]!, pts[i]!, pts[i + 1]!);
  return { pts: pts.slice(), h };
}

function dedupe(pts: number[], minStep: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < pts.length; i += 2) {
    const m = out.length;
    if (m === 0 || Math.hypot(pts[i]! - out[m - 2]!, pts[i + 1]! - out[m - 1]!) >= minStep) out.push(pts[i]!, pts[i + 1]!);
  }
  while (out.length >= 6 && Math.hypot(out[0]! - out[out.length - 2]!, out[1]! - out[out.length - 1]!) < minStep) out.splice(out.length - 2, 2);
  return out;
}

/* ------------------------------------------------------- Schneider fit */

function fitCubic(d: Vec[], first: number, last: number, tan1: Vec, tan2: Vec, error: number, out: Cubic[]): void {
  // a straight run is a line: anchors only, no handles; so is any run too short to bend
  if (last - first === 1 || isStraight(d, first, last, error) || len(sub(d[last]!, d[first]!)) < 3 * error) {
    out.push({ p0: d[first]!, c1: d[first]!, c2: d[last]!, p3: d[last]! });
    return;
  }
  let u = chordLengthParameterize(d, first, last);
  // reparameterise rather than split while the fit is only modestly off
  let maxError = Math.max(4 * error, error * error);
  let split = first + 1;
  let inOrder = true;
  for (let i = 0; i <= 8; i++) {
    const curve = generateBezier(d, first, last, u, tan1, tan2);
    const max = findMaxError(d, first, last, curve, u);
    // the curve must also stay on the border BETWEEN the points it was fitted to: a run of
    // few points can be met at each of them by a curve that bulges far away in between
    const dense = denseError(curve, d, first, last);
    if (max.error < error && dense < error && inOrder) {
      out.push(curve);
      return;
    }
    split = max.index;
    if (dense >= error && max.error < error) {
      split = Math.max(first + 1, Math.min(last - 1, Math.floor((first + last) / 2)));
      break;
    }
    if (max.error >= maxError) break;
    inOrder = reparameterize(d, first, last, u, curve);
    maxError = max.error;
  }
  const tanCenter = unit(sub(d[Math.max(first, split - 2)]!, d[Math.min(last, split + 2)]!));
  fitCubic(d, first, split, tan1, tanCenter, error, out);
  fitCubic(d, split, last, mul(tanCenter, -1), tan2, error, out);
}

function isStraight(d: Vec[], first: number, last: number, tol: number): boolean {
  const a = d[first]!,
    b = d[last]!;
  const l = len(sub(b, a)) || 1;
  for (let i = first + 1; i < last; i++) {
    const p = d[i]!;
    const cross = Math.abs((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x)) / l;
    if (cross > tol) return false;
  }
  return true;
}

function generateBezier(d: Vec[], first: number, last: number, u: number[], tan1: Vec, tan2: Vec): Cubic {
  const p1 = d[first]!,
    p2 = d[last]!;
  let c00 = 0,
    c01 = 0,
    c11 = 0,
    x0 = 0,
    x1 = 0;
  for (let i = first; i <= last; i++) {
    const t = u[i - first]!,
      s = 1 - t;
    const b0 = s * s * s,
      b1 = 3 * t * s * s,
      b2 = 3 * t * t * s,
      b3 = t * t * t;
    const a1 = mul(tan1, b1),
      a2 = mul(tan2, b2);
    c00 += dot(a1, a1);
    c01 += dot(a1, a2);
    c11 += dot(a2, a2);
    const tmp = sub(d[i]!, add(mul(p1, b0 + b1), mul(p2, b2 + b3)));
    x0 += dot(a1, tmp);
    x1 += dot(a2, tmp);
  }
  const det = c00 * c11 - c01 * c01;
  let alpha1: number, alpha2: number;
  if (Math.abs(det) > 1e-12) {
    alpha1 = (x0 * c11 - x1 * c01) / det;
    alpha2 = (c00 * x1 - c01 * x0) / det;
  } else {
    const c0 = c00 + c01,
      c1 = c01 + c11;
    alpha1 = Math.abs(c0) > 1e-12 ? x0 / c0 : Math.abs(c1) > 1e-12 ? x1 / c1 : 0;
    alpha2 = alpha1;
  }
  const segLength = len(sub(p2, p1));
  const eps = 1e-6 * segLength;
  let handle1: Vec | null = null,
    handle2: Vec | null = null;
  // handles longer than the chord itself only happen when the tangents mislead the fit: an ear, never a curve
  if (alpha1 < eps || alpha2 < eps || alpha1 > 0.75 * segLength || alpha2 > 0.75 * segLength) alpha1 = alpha2 = segLength / 3;
  else {
    const line = sub(p2, p1);
    handle1 = mul(tan1, alpha1);
    handle2 = mul(tan2, alpha2);
    if (dot(handle1, line) - dot(handle2, line) > segLength * segLength) {
      alpha1 = alpha2 = segLength / 3;
      handle1 = handle2 = null;
    }
  }
  return { p0: p1, c1: add(p1, handle1 ?? mul(tan1, alpha1)), c2: add(p2, handle2 ?? mul(tan2, alpha2)), p3: p2 };
}

function chordLengthParameterize(d: Vec[], first: number, last: number): number[] {
  const u = [0];
  for (let i = first + 1; i <= last; i++) u.push(u[i - first - 1]! + len(sub(d[i]!, d[i - 1]!)));
  const total = u[last - first]! || 1;
  return u.map((x) => x / total);
}

function reparameterize(d: Vec[], first: number, last: number, u: number[], curve: Cubic): boolean {
  for (let i = first; i <= last; i++) u[i - first] = findRoot(curve, d[i]!, u[i - first]!);
  for (let i = 1; i < u.length; i++) if (u[i]! <= u[i - 1]!) return false;
  return true;
}

function findRoot(curve: Cubic, point: Vec, u: number): number {
  const c = [curve.p0, curve.c1, curve.c2, curve.p3];
  const c1 = [0, 1, 2].map((i) => mul(sub(c[i + 1]!, c[i]!), 3));
  const c2 = [0, 1].map((i) => mul(sub(c1[i + 1]!, c1[i]!), 2));
  const p = evaluate(3, c, u),
    p1 = evaluate(2, c1, u),
    p2 = evaluate(1, c2, u);
  const diff = sub(p, point);
  const df = dot(p1, p1) + dot(diff, p2);
  if (Math.abs(df) < 1e-9) return u;
  return u - dot(diff, p1) / df;
}

function evaluate(degree: number, c: Vec[], t: number): Vec {
  const tmp = c.slice();
  for (let i = 1; i <= degree; i++) for (let j = 0; j <= degree - i; j++) tmp[j] = add(mul(tmp[j]!, 1 - t), mul(tmp[j + 1]!, t));
  return tmp[0]!;
}

/** Largest distance from the curve (sampled) to the polyline d[first..last]. */
function denseError(curve: Cubic, d: Vec[], first: number, last: number): number {
  const c = [curve.p0, curve.c1, curve.c2, curve.p3];
  let worst = 0;
  for (let k = 1; k < 24; k++) {
    const q = evaluate(3, c, k / 24);
    let best = Infinity;
    for (let i = first; i < last; i++) {
      const a = d[i]!,
        b = d[i + 1]!;
      const ab = sub(b, a);
      const l2 = dot(ab, ab) || 1;
      const t = Math.max(0, Math.min(1, dot(sub(q, a), ab) / l2));
      best = Math.min(best, len(sub(q, add(a, mul(ab, t)))));
    }
    worst = Math.max(worst, best);
  }
  return worst;
}

function findMaxError(d: Vec[], first: number, last: number, curve: Cubic, u: number[]): { error: number; index: number } {
  let index = Math.floor((last - first + 1) / 2),
    maxDist = 0;
  for (let i = first + 1; i < last; i++) {
    const p = evaluate(3, [curve.p0, curve.c1, curve.c2, curve.p3], u[i - first]!);
    const dist = len(sub(p, d[i]!));
    if (dist >= maxDist) {
      maxDist = dist;
      index = i;
    }
  }
  return { error: maxDist, index };
}

/* --------------------------------------------------------- use of paths */

/**
 * Points along a closed path (for hit tests, bounds, merging), about `maxSeg` px apart on
 * curves. `anchorIdx`, when given, receives the polygon index of every anchor.
 */
export function flattenPath(pts: number[], h: number[], maxSeg = 5, anchorIdx?: number[]): number[] {
  const n = pts.length / 2;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const p0 = v(pts[2 * i]!, pts[2 * i + 1]!),
      p3 = v(pts[2 * j]!, pts[2 * j + 1]!);
    const c1 = v(h[4 * i + 2]!, h[4 * i + 3]!),
      c2 = v(h[4 * j]!, h[4 * j + 1]!);
    anchorIdx?.push(out.length / 2);
    out.push(p0.x, p0.y);
    const chordLen = len(sub(p3, p0)) || 1;
    const offLine = (c: Vec) => Math.abs((p3.x - p0.x) * (c.y - p0.y) - (p3.y - p0.y) * (c.x - p0.x)) / chordLen;
    const straightSeg = offLine(c1) < 0.05 && offLine(c2) < 0.05;
    if (straightSeg) continue;
    const chord = len(sub(p3, p0)) + len(sub(c1, p0)) + len(sub(c2, c1)) + len(sub(p3, c2));
    const steps = Math.max(2, Math.min(48, Math.ceil(chord / maxSeg)));
    for (let s = 1; s < steps; s++) {
      const t = s / steps,
        q = 1 - t;
      out.push(q * q * q * p0.x + 3 * q * q * t * c1.x + 3 * q * t * t * c2.x + t * t * t * p3.x, q * q * q * p0.y + 3 * q * q * t * c1.y + 3 * q * t * t * c2.y + t * t * t * p3.y);
    }
  }
  return out;
}

/** The closed path on a canvas. */
export function pathOn(ctx: CanvasRenderingContext2D, pts: number[], h: number[], begin = true): void {
  const n = pts.length / 2;
  if (begin) ctx.beginPath();
  if (n === 0) return;
  ctx.moveTo(pts[0]!, pts[1]!);
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    ctx.bezierCurveTo(h[4 * i + 2]!, h[4 * i + 3]!, h[4 * j]!, h[4 * j + 1]!, pts[2 * j]!, pts[2 * j + 1]!);
  }
  ctx.closePath();
}

/** Move one anchor (with its handles) to a new position. */
export function moveAnchor(path: CurvePath, i: number, to: Vec): CurvePath {
  const pts = path.pts.slice(),
    h = path.h.slice();
  const dx = to.x - pts[2 * i]!,
    dy = to.y - pts[2 * i + 1]!;
  pts[2 * i] = to.x;
  pts[2 * i + 1] = to.y;
  h[4 * i] = h[4 * i]! + dx;
  h[4 * i + 1] = h[4 * i + 1]! + dy;
  h[4 * i + 2] = h[4 * i + 2]! + dx;
  h[4 * i + 3] = h[4 * i + 3]! + dy;
  return { pts, h };
}

/** Remove one anchor; its neighbours keep their handles and the curve re-spans the gap. */
export function removeAnchor(path: CurvePath, i: number): CurvePath {
  const pts = path.pts.slice(),
    h = path.h.slice();
  pts.splice(2 * i, 2);
  h.splice(4 * i, 4);
  return { pts, h };
}

export function translatePath(path: CurvePath, dx: number, dy: number): CurvePath {
  return { pts: path.pts.map((c, k) => c + (k % 2 ? dy : dx)), h: path.h.map((c, k) => c + (k % 2 ? dy : dx)) };
}
